import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import { useGroupMessages } from "../../hooks/useCoralApi";
import MembersModal from "./MembersModal";
import ComposeEmailModal from "../compose-email-modal/compose-email-modal";
import coralService from "../../../../services/coral/coral-service";
import { formatMessageTime } from "../../../../services/coral/coral-utils";
import "./group-detail.scss";

// Utility functions
const getInitials = (name) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

  const GroupDetail = ({
    group,
    onAddMember,
    onRemoveMember,
    participants,
    participantsLoading,
    onRefreshMembers,
    loading: initialLoading,
    isGroupSwitching,
    showMembersModal,
    setShowMembersModal,
    setShowEditModal,
    setShowDeleteModal,
    organizationSettings,
    genericModals: { setShowSuccessModal, setSuccessModalHeaderAndMessage, setShowErrorModal, setErrorModalHeaderAndMessage }
  }) => {
  const [newMessage, setNewMessage] = useState("");
  const [animationKey, setAnimationKey] = useState(0);
  const messagesContainerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [showComposeEmailModal, setShowComposeEmailModal] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [loadingAlert, setLoadingAlert] = useState(false);
  const [sendingCswTemplate, setSendingCswTemplate] = useState(false);
  const [cswTemplateSent, setCswTemplateSent] = useState(false);
  const [, setNowTick] = useState(() => Date.now());
  
  const organizationId = useSelector(
    (state) => state?.userStore?.userInfo?._organizations?.[0] || ""
  );

  const hasTemplate = () => {
    return !!getTemplate();
  };
  const getTemplate = () => {
    return organizationSettings?.cswRequestTemplateName?.trim();
  };

  // Callback for when new messages arrive from real-time updates
  const handleNewMessages = useCallback(() => {
    // Auto-scroll to bottom when new messages arrive
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setShouldScrollToBottom(true);
    }
  }, []);

  // API Hooks
  const {
    messages,
    loading: messagesLoading,
    isLoadingMore,
    sendMessage,
    sendTemplate,
    loadMore,
    hasMore,
    error: messagesError
  } = useGroupMessages(group?.id, handleNewMessages);

  // Force re-render animation when group changes
  useEffect(() => {
    setAnimationKey(prev => prev + 1);
    setShouldScrollToBottom(true);
    setCswTemplateSent(false);
    setSendingCswTemplate(false);
  }, [group?.id]);

  useEffect(() => {
    const intervalId = setInterval(() => setNowTick(Date.now()), 30_000);
    return () => clearInterval(intervalId);
  }, []);

  // Scroll to bottom when new messages arrive (only for sent messages)
  useEffect(() => {
    if (shouldScrollToBottom && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, shouldScrollToBottom]);

  // Handle scroll for infinite loading and scroll-to-bottom button
  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Check if scrolled to top for loading more messages
    if (container.scrollTop < 100 && hasMore && !isLoadingMore) {
      const previousScrollHeight = container.scrollHeight;
      loadMore();
      // Maintain scroll position after loading
      setTimeout(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - previousScrollHeight;
      }, 100);
    }

    // Check if user is near bottom
    const isNearBottom = container.scrollHeight - container.scrollTop - container.clientHeight < 100;
    setShouldScrollToBottom(isNearBottom);
    setShowScrollToBottom(!isNearBottom && container.scrollHeight > container.clientHeight);
  }, [hasMore, isLoadingMore, loadMore]);

  // Scroll to bottom function
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      setShouldScrollToBottom(true);
    }
  };

  // Message Handlers
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!group?.cswOpen) return;
    if (!newMessage.trim()) return;

    try {
      await sendMessage(newMessage.trim());
      setNewMessage("");
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to send message: " + error.message
      });
    }
  };

  const handleSendCswTemplate = async () => {
    if (!group?.id || group?.cswOpen || sendingCswTemplate || cswTemplateSent || !hasTemplate()) return;

    try {
      setSendingCswTemplate(true);
      await sendTemplate(getTemplate(), []);
      setCswTemplateSent(true);
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to send customer service window request: " + error.message
      });
    } finally {
      setSendingCswTemplate(false);
    }
  };

  // Alert Email Handlers
  const handleAlertEmailClick = async (alertId, e) => {
    e.stopPropagation();
    setLoadingAlert(true);

    try {
      const alertDetails = await coralService.getAlert(organizationId, alertId);
      setSelectedAlert(alertDetails);
      setShowComposeEmailModal(true);
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to load alert details: " + error.message
      });
    } finally {
      setLoadingAlert(false);
    }
  };

  const handleSendEmail = async (alertId, recipientEmail, subject, body) => {
    try {
      await coralService.sendAlertEmail(organizationId, alertId, recipientEmail, subject, body);
      setShowComposeEmailModal(false);
      setSelectedAlert(null);
      setShowSuccessModal(true);
      setSuccessModalHeaderAndMessage({
        header: "Success",
        message: "Email sent successfully!"
      });
    } catch (error) {
      setShowErrorModal(true);
      setErrorModalHeaderAndMessage({
        header: "Error",
        message: "Failed to send email: " + error.message
      });
      throw error;
    }
  };

  const handleCloseEmailModal = () => {
    setShowComposeEmailModal(false);
    setSelectedAlert(null);
  };


  // Render Methods
  const renderGroupHeader = () => (
    <div className="group-header chat-header flex-wrap">
      <div className="media group-info gap-0">
        <div className="avatar-thumb thumb-md justify-content-center d-flex align-items-center bg-soft-primary rounded-circle">
          {group?.avatar ? (
            <img src={group.avatar} alt={group?.name} />
          ) : (
            getInitials(group?.name || 'Group')
          )}
        </div>
        <div className="media-body group-details">
          <h6 className="m-0">{group?.name || 'Loading...'}</h6>
          <p className="group-description">{group?.description || ''}</p>
          <span className="member-count small">{participants.filter(p => !p.invitee).length} members ({participants.length} invited)</span>
        </div>
      </div>
      <div className="group-actions gap-1">
        {/* <button className="bouncy-button small white" title="Group Info">
          <i className="fas fa-info-circle"></i>
        </button>
        <button className="bouncy-button small white" title="Search Messages">
          <i className="fas fa-search"></i>
        </button> */}
        <button 
          className="bouncy-button small white" 
          title="View Members"
          onClick={() => setShowMembersModal(true)}
        >
          <i className="fas fa-users"></i>
        </button>
        <button className="bouncy-button small white"
          title="More Options"
          onClick={() => setShowEditModal(true)}
        >
          <i className="fas fa-ellipsis-v"></i>
        </button>
        <button
          className="bouncy-button small red"
          title="Delete Group"
          onClick={() => setShowDeleteModal(true)}
        >
          <i className="fas fa-trash"></i>
        </button>
      </div>
    </div>
  );

  const renderLoadingState = () => (
    <div className="thread-loader-wrapper">
      <img
        src="assets/images/ring-loader.svg"
        alt="loading more"
        className="loader"
      />
    </div>
  );

  const renderErrorState = () => (
    <div className="no-messages">
      <i className="fas fa-exclamation-triangle"></i>
      <p>Failed to load messages. Please try again.</p>
    </div>
  );

  const renderEmptyState = () => (
    <div className="no-messages">
      <i className="fas fa-comments text-brand"></i>
      <p>No messages yet. Start the conversation!</p>
    </div>
  );

  const renderMessage = (message) => {
    // Email response messages have a special centered layout
    if (message.isEmailResponse) {
      return (
        <div key={message.id} className="message email-response">
          <div className="chip-header">
            <i className="fas fa-envelope"></i>
            {/* <span>Response from Email</span> */}
            <span>{message.sender}</span>
          </div>
          <div className="email-response-chip">
            <div className="chip-content">
              <p>{message.content}</p>
            </div>
          </div>
          <div className="chip-time">{formatMessageTime(message.rawTimestamp || message.timestamp)}</div>
        </div>
      );
    }

    // Platform message orignated from HubSpot
    if (message.isHubspot) {
      return (
        <div key={message.id} className="message hubspot">
          <div className="message-sender">
            <i className="fas fa-user"></i>
            <span>{message.sender}</span>
          </div>
          <div className="message-content">
            <p>{message.content}</p>
          </div>
          <div className="message-time">{formatMessageTime(message.rawTimestamp || message.timestamp)}</div>
        </div>
      );
    }

    // Regular messages
    const platformIsSender = message.isOwn || message.isPlatform || message.isHubspot;
    const messageType = message.isPlatform ? "platform" : "other";
    const isTemplate = message.isTemplate;
    const displayedContent = isTemplate ? (message.displayed_content ?? message.content) : message.content;
    const attachments = message.attachments || [];
    const uploadedAttachments = attachments.filter(a => a.upload_status === "uploaded" && a.stable_url);
    return (
      <div
        key={message.id}
        className={`message ${messageType} ${message.isAlert ? "alert" : ""} ${isTemplate ? "template" : ""}`}
      >
        {!platformIsSender && !message.isTemplate && (
          <div className="message-sender"><i className="fas fa-user"></i> {message.sender}</div>
        )}
        <div className="message-content">
          {displayedContent ? <p>{displayedContent}</p> : null}
          {uploadedAttachments.length > 0 && (
            <div className="message-attachments">
              {uploadedAttachments.map((attachment, idx) => {
                if (attachment.attachment_type === "image") {
                  return (
                    <a key={idx} href={attachment.stable_url} target="_blank" rel="noopener noreferrer" className="attachment-image-link">
                      <img
                        src={attachment.stable_url}
                        alt="attachment"
                        className="attachment-image"
                      />
                    </a>
                  );
                }
                if (attachment.attachment_type === "video") {
                  return (
                    <video key={idx} src={attachment.stable_url} controls className="attachment-video">
                      <track kind="captions" />
                    </video>
                  );
                }
                if (attachment.attachment_type === "audio") {
                  return (
                    <audio key={idx} src={attachment.stable_url} controls className="attachment-audio" />
                  );
                }
                return (
                  <a key={idx} href={attachment.stable_url} target="_blank" rel="noopener noreferrer" className="attachment-file">
                    <i className="fas fa-file"></i>
                    <span>{attachment.attachment_type || "File"}</span>
                  </a>
                );
              })}
            </div>
          )}
          {message.isAlert && (
            <div className="alert-actions">
              <span className="alert-badge">
                <i className="fas fa-exclamation-triangle"></i>
              </span>
              {message.alertId && (
                <button
                  className="icon-button yellow"
                  onClick={(e) => handleAlertEmailClick(message.alertId, e)}
                  disabled={loadingAlert}
                  title="Send Alert Email"
                >
                  <i className={`fas ${loadingAlert ? 'fa-spinner fa-spin' : 'fa-envelope'}`}></i>
                </button>
              )}
            </div>
          )}
          <span className="message-time">
            {formatMessageTime(message.rawTimestamp || message.timestamp)}
            <span className="status">
              <span className="read ms-1" title="Read at 12:00 pm">
                <i className="fa fa-check-double"></i>
              </span>
            </span>
          </span>
        </div>
      </div>
    );
  };

  const renderMessages = () => {
    if (messagesLoading && messages.length === 0) {
      return renderLoadingState();
    }

    if (messagesError) {
      return renderErrorState();
    }

    if (messages.length === 0) {
      return renderEmptyState();
    }

    return (
      <>
        {isLoadingMore && (
          <div className="loading-more">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading older messages...</span>
          </div>
        )}
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </>
    );
  };

  const renderMessageInput = () => {
    const isCswOpen = !!group?.cswOpen;

    return (
      <div className="message-input-container">
        <form onSubmit={handleSendMessage} className="message-form align-items-center">
          <div className="input-wrapper" style={{ position: 'relative' }}>
            <span className="chat-admin">
              <img
                src="assets/images/profile-avatar.png"
                alt="user"
                className="rounded-circle thumb-sm"
              />
            </span>
            
            <input
              type="text"
              placeholder={isCswOpen ? "Type a message..." : "Free-form messaging not possible - the customer service window is closed and opens when an external participant sends a message."}
              value={newMessage}
              onChange={(e) => isCswOpen && setNewMessage(e.target.value)}
              className="message-input form-control"
              disabled={!isCswOpen}
            />
            {/* {isCswOpen && <button
                type="button"
                className="icon-button"
                title={isCswOpen ? "Attach File" : "Messaging disabled"}
                disabled={!isCswOpen}
              >
                <i className="fas fa-paperclip"></i>
              </button>} */}
            {!isCswOpen && !cswTemplateSent && (
              <div
                style={{
                  position: 'absolute',
                  left: '60px',
                  right: '10px',
                  bottom: '40px',
                  background: 'var(--clr-yellow-lighter)',
                  borderRadius: '999px',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  fontSize: '12px',
                  gap: '8px',
                  cursor: hasTemplate() && !sendingCswTemplate ? 'pointer' : 'default',
                }}
                onClick={hasTemplate() && !sendingCswTemplate ? handleSendCswTemplate : undefined}
              >
                <span style={{ flex: 1 }}>
                  {hasTemplate()
                    ? "You can request the window be opened, click here to send a template instead."
                    : "You must set a template to request the customer service window is opened to alert group participants you wish to send a message"}
                </span>
                {hasTemplate() && (
                  <button
                    type="button"
                    className="bouncy-button small"
                    disabled={sendingCswTemplate}
                    title="Send template to request customer service window"
                  >
                    <i className={`fas ${sendingCswTemplate ? 'fa-spinner fa-spin' : 'fa-paper-plane'}`}></i>
                  </button>
                )}
              </div>
            )}
            {!isCswOpen && cswTemplateSent && (
              <div
                style={{
                  position: 'absolute',
                  left: '60px',
                  right: '10px',
                  bottom: '40px',
                  background: 'var(--clr-green-lighter)',
                  borderRadius: '999px',
                  padding: '6px 10px',
                  display: 'flex',
                  alignItems: 'center',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                  fontSize: '12px',
                  gap: '8px',
                  cursor: 'default',
                }}
              >
                <span style={{ flex: 1 }}>
                  Customer service window request sent.
                </span>
              </div>
            )}
          </div>
          {isCswOpen && newMessage.trim() !== "" && ( // Conditionally show the send button
            <button type="submit" className="bouncy-button small" title="Send Message">
              <i className="fas fa-paper-plane"></i>
            </button>
          )}
        </form>
      </div>
    );
  };

  const renderGroupSwitchingLoader = () => (
    <div className="fullscreen-loader">
      <div className="thread-loader-wrapper">
        <img
          src="assets/images/ring-loader.svg"
          alt="loader"
          className="loader"
        />
      </div>
    </div>
  );

  return (
    <div className="group-detail-container">
      {/* Fullscreen Loader for Group Switching */}
      {isGroupSwitching && renderGroupSwitchingLoader()}
      
      {/* Group Header */}
      {renderGroupHeader()}

      {/* Content Area - Messages */}
      <div className="content-area">
        <div key={`messages-${animationKey}`} className="messages-section">
          <div
            className="messages-container"
            ref={messagesContainerRef}
            onScroll={handleScroll}
          >
            {renderMessages()}
          </div>

          {/* Scroll to bottom button */}
          {showScrollToBottom && (
            <button
              className="scroll-to-bottom-btn"
              onClick={scrollToBottom}
              title="Scroll to bottom"
            >
              <i className="fas fa-chevron-down"></i>
            </button>
          )}

          {/* Message Input */}
          {renderMessageInput()}
        </div>
      </div>

      {/* Members Modal */}
      <MembersModal
        isOpen={showMembersModal}
        onClose={() => setShowMembersModal(false)}
        participants={participants}
        participantsLoading={participantsLoading}
        onAddMember={onAddMember}
        onRemoveMember={onRemoveMember}
        onRefreshMembers={onRefreshMembers}
      />

      {/* Compose Email Modal */}
      <ComposeEmailModal
        isOpen={showComposeEmailModal}
        onClose={handleCloseEmailModal}
        notification={selectedAlert}
        onSendEmail={handleSendEmail}
        errorModal={{ setShowErrorModal, setErrorModalHeaderAndMessage }}
      />


    </div>
  );
};

export default GroupDetail;
